import './overlapped-seasons.scss';

import InfoIcon from '@mui/icons-material/Info';
import {
	FormControl,
	MenuItem,
	Select,
	type SelectChangeEvent,
} from '@mui/material';

import { CustomTooltip } from '@/components/custom-tooltip';
import type { PropSeason } from 'graphql/gql-types';
import { labels } from 'locales/en.label';
import { SeasonPriority } from 'types/SeasonTypes';

type OverlappedSeasonsProps = {
	overlappedSeasons: PropSeason[];
	onSeasonPriorityChange: (
		id: string,
		event: SelectChangeEvent<number>,
	) => void;
};

// Helper function to identify default season
const isDefaultSeason = (season: PropSeason): boolean => {
	return season.season_name === labels.rules.default_season_name;
};

// Helper function to get available priorities for user-created seasons (excludes priority 1)
const getAvailableUserPriorities = () => {
	return Object.entries(SeasonPriority).filter(
		([_key, value]) => !Number.isNaN(Number(value)) && Number(value) > 1,
	);
};

export const OverlappedSeasons = ({
	overlappedSeasons,
	onSeasonPriorityChange,
}: OverlappedSeasonsProps) => {
	return (
		<>
			<div className='overlapping-modal'>
				<p className='overlapping-modal__message'>
					{labels.rules.overlap_confirmation_message}
				</p>
				<div className='overlapping-modal__container'>
					<div className='overlapping-modal__emptytext' />
					<div className='overlapping-modal__text'>
						<div className='overlapping-modal__text__priority'>
							{labels.rules.priority}
						</div>
						<div className='overlapping-modal__text__container'>
							<div className='overlapping-modal__subheader'>
								{labels.rules.current}
							</div>
							<div className='overlapping-modal__subheader'>
								{labels.rules.new}
							</div>
						</div>
					</div>
				</div>

				{overlappedSeasons?.map((season) => {
					const isDefault = isDefaultSeason(season);

					return (
						<div
							key={season.season_id}
							style={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
							}}
						>
							<div className='overlapping-modal__emptytext'>
								{season.season_name}
							</div>
							<FormControl
								sx={{ m: 1, minWidth: 100 }}
								disabled
								className='overlapping-modal__primarytext overlapping-modal__disabledSelect'
							>
								<Select
									labelId='demo-simple-select-label'
									id='demo-simple-select'
									value={season.season_priority}
								>
									{Object.entries(SeasonPriority)?.map(
										([key, value]) =>
											!Number.isNaN(Number(value)) && (
												<MenuItem key={key} value={value}>
													{key}
												</MenuItem>
											),
									)}
								</Select>
							</FormControl>
							<FormControl
								sx={{ m: 1, minWidth: 100 }}
								disabled={isDefault}
								className={`overlapping-modal__primarytext ${
									isDefault ? 'overlapping-modal__protectedSeason' : ''
								}`}
							>
								<Select
									value={season.season_priority}
									onChange={
										isDefault
											? undefined
											: (e) => onSeasonPriorityChange(season.season_id!, e)
									}
									autoWidth
									MenuProps={
										isDefault
											? undefined
											: {
													disableScrollLock: true,
												}
									}
								>
									{(isDefault
										? Object.entries(SeasonPriority)?.filter(
												([_key, value]) => !Number.isNaN(Number(value)),
											)
										: getAvailableUserPriorities()
									)?.map(([key, value]) => (
										<MenuItem
											key={key}
											value={value}
											className='overlapping-modal__primarytext'
										>
											{key}
										</MenuItem>
									))}
								</Select>
								{isDefault && (
									<CustomTooltip
										content='Default season priority is always Lowest and cannot be changed'
										placement='top'
									>
										<InfoIcon className='overlapping-modal__protectedIcon' />
									</CustomTooltip>
								)}
							</FormControl>
						</div>
					);
				})}
			</div>
		</>
	);
};
